import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Global } from '@emotion/react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useRouter } from 'next/router';
import Lottie from 'lottie-react';
import {
  GlobalStyle, PageWrapper, BackgroundEffect, HeaderContainer, LogoFrame, LogoIcon, HeaderTitle,
  MainContent, MainTitle, FormContainer, StepHeaderContainer, StepInfo, StepTag, StepTagText,
  StepTitle, ProcessBar, Footer, FooterLogo, Copyright
} from '@/styles/SharedStyle';

// --- Local Styled Components ---

const ServiceArchSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 80px;
  margin-bottom: 100px;
  width: 100%;
  max-width: 1920px;
`;

const SectionTitle = styled.h2`
  font-family: 'Reddit Sans', sans-serif;
  font-weight: 700;
  font-size: 36px;
  color: #ffffff;
  margin-bottom: 60px;
  line-height: 1;
`;

const ArchImage = styled.img`
  width: auto;
  max-width: 1200px;
  height: auto;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionLabel = styled.p`
  font-family: 'Reddit Sans', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #ffffff;
  margin: 0;
`;

const TypeSelectionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

const TypeButton = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 220px;
  height: 52px;
  border-radius: 100px;
  background-color: ${props => props.active ? '#0089CF' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  
  span {
    font-family: 'Pretendard', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const TypeIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const ThumbnailGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const ThumbnailItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.2);
  background-color: #222;
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbIconWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #333;
  color: #fff;
  font-size: 10px;
  padding: 4px;
  text-align: center;
`;

const RemoveThumbBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0,0,0,0.6);
  border: none;
  border-radius: 50%;
  color: white;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  &:hover { background: rgba(255,0,0,0.8); }
`;

const AddThumbPlaceholder = styled.label`
  width: 80px;
  height: 80px;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  margin-top: 20px;
  margin-bottom: 12px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
  }
`;

const TextInputWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 32px;
  height: 400px;
  display: flex;
  flex-direction: column;
`;

const TextInputArea = styled.textarea`
  width: 100%;
  flex: 1;
  border: none;
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  color: #333;
  resize: none;
  &::placeholder {
    color: #999999;
  }
  &:focus {
    outline: none;
  }
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 10px;
`;

const UploadButton = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background-color: #0089CF;
  border-radius: 8px;
  width: 200px;
  height: 52px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const UploadIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const UploadText = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #ffffff;
`;

const SettingsRow = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`;

const SelectContainer = styled.div`
  width: 300px;
  position: relative;
  background-color: #222222;
  border-radius: 8px;
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 20px;
`;

const HiddenSelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const SelectValue = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #ffffff;
  margin-right: auto;
`;

const SelectArrow = styled.img`
  width: 24px;
  height: 24px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 60px; /* Below container */
  left: 0;
  width: 100%;
  background-color: #222222;
  border-radius: 8px;
  overflow: hidden;
  z-index: 100;
  border: 1px solid #333333;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
`;

const DropdownItem = styled.div<{ selected?: boolean }>`
  padding: 14px 20px;
  cursor: pointer;
  color: ${props => props.selected ? '#0089CF' : '#ffffff'};
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  font-size: 18px;
  background-color: ${props => props.selected ? 'rgba(0, 137, 207, 0.1)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: #333333;
  }
`;

const GenerateButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 56px;
  background-color: #0089CF;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  margin-top: 20px;
  transition: background-color 0.2s;

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background-color: #0077b3;
  }
`;

const GenerateIcon = styled.img`
  width: 28px;
  height: 28px;
`;

const GenerateText = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #ffffff;
`;

const CHANNELS = ['블로그', '인스타그램', '카드뉴스', '광고 카피'];
const TONES = ['전문적인', '친근한', '유머러스한', '감성적인', '간결한'];

export default function Home() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const [sourceText, setSourceText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; data: string; type: string }[]>([]);

  const [channel, setChannel] = useState('');
  const [tone, setTone] = useState('');
  const [selectedType, setSelectedType] = useState('text-image');
  const [loaderData, setLoaderData] = useState(null);
  const [loadingBarData, setLoadingBarData] = useState(null);
  const [openChannel, setOpenChannel] = useState(false);
  const [openTone, setOpenTone] = useState(false);

  useEffect(() => {
    axios.get('/aicontent/loader.json').then(res => setLoaderData(res.data)).catch(console.error);
    axios.get('/aicontent/loading-bar.json').then(res => setLoadingBarData(res.data)).catch(console.error);
  }, []);

  // Load saved state on mount
  React.useEffect(() => {
    try {
      const savedState = localStorage.getItem('step1State');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        setSourceText(parsed.sourceText || '');
        setChannel(parsed.channel || '');
        setTone(parsed.tone || '');
        setSelectedType(parsed.selectedType || 'text-image');
      }
      const savedFiles = localStorage.getItem('attachedFiles');
      if (savedFiles) {
        setAttachedFiles(JSON.parse(savedFiles));
      }
    } catch (e) {
      console.error("Failed to load saved state", e);
    }
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          if (ev.target?.result) {
            setAttachedFiles(prev => [...prev, {
              name: file.name,
              data: ev.target!.result as string,
              type: file.type
            }]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeFile = (fileToRemove: { name: string; data: string; type: string }) => {
    setAttachedFiles(prev => prev.filter(f => f !== fileToRemove));
  };

  const handleGenerate = async () => {
    if (!sourceText && attachedFiles.length === 0) {
      alert('텍스트나 파일을 입력해주세요.');
      return;
    }
    if (true) {
      alert('포인트가 부족합니다. 관리자에게 문의하세요.');
      return;
    }
    setLoading(true);
    try {
      const sourceImages: string[] = [];
      const sourceDocs: { name: string; data: string; type: string }[] = [];
      attachedFiles.forEach(file => {
        if (file.type.startsWith('image/')) {
          sourceImages.push(file.data);
        } else {
          sourceDocs.push(file);
        }
      });

      const res = await axios.post('/aicontent/api/generate', {
        type: 'generate-draft',
        channel,
        tone,
        sourceText,
        sourceImages,
        sourceDocs
      });

      // Save draft and navigation to Step 2
      localStorage.setItem('draftData', JSON.stringify(res.data));

      const step1State = {
        sourceText,
        channel,
        tone,
        selectedType
      };
      localStorage.setItem('step1State', JSON.stringify(step1State));

      // attachedFiles might be too large for localStorage if many images. 
      // Ideally use Context or passing URL if uploaded. For now, try localStorage catch error.
      try {
        localStorage.setItem('attachedFiles', JSON.stringify(attachedFiles));
      } catch (e) {
        console.error("Failed to save files to localStorage", e);
      }

      router.push('/step2');

    } catch (error) {
      console.error(error);
      alert('생성 실패');
    } finally {
      setLoading(false);
    }
  };

  // Filter files based on active tab
  const filteredFiles = attachedFiles.filter(file => {
    if (selectedType === 'text-image') return file.type.startsWith('image/');
    return !file.type.startsWith('image/');
  });

  return (
    <>
      <Global styles={GlobalStyle} />
      <PageWrapper>
        <BackgroundEffect src="/aicontent/assets/background_effect_v2.png" alt="" />

        {/* Header restored based on latest Figma data */}
        <HeaderContainer>
          <LogoFrame>
            <LogoIcon src="/aicontent/assets/logo_symbol.svg" alt="Logo" />
            <HeaderTitle>LLM 기반 기업 콘텐츠 자동 생성 AI 시스템 구축</HeaderTitle>
          </LogoFrame>
        </HeaderContainer>

        <ServiceArchSection>
          <SectionTitle>Service Architecture</SectionTitle>
          <ArchImage src="/aicontent/assets/architecture_diagram_v3.png" alt="Service Architecture" />
        </ServiceArchSection>

        <MainContent>
          <MainTitle>AI Content Generator</MainTitle>

          <FormContainer>
            <StepHeaderContainer>
              <StepInfo>
                <StepTag>
                  <StepTagText>STEP 1</StepTagText>
                </StepTag>
                <StepTitle>소스 등록 및 설정</StepTitle>
              </StepInfo>
              <ProcessBar src="/aicontent/assets/process_bar.svg" alt="Process Step 1" />
            </StepHeaderContainer>

            <InputSection>
              <SectionLabel>원천 소스를 등록해 주세요.</SectionLabel>

              {/* Type Selection (Visual Tabs) */}
              <TypeSelectionRow>
                <TypeButton active={selectedType === 'text-image'} onClick={() => setSelectedType('text-image')}>
                  <TypeIcon
                    src={selectedType === 'text-image' ? "/aicontent/assets/icon_text_image.svg" : "/aicontent/assets/icon_text_image_inactive.svg"}
                    alt=""
                  />
                  <span>텍스트/이미지</span>
                </TypeButton>
                <TypeButton active={selectedType === 'document'} onClick={() => setSelectedType('document')}>
                  <TypeIcon
                    src={selectedType === 'document' ? "/aicontent/assets/icon_document_active.svg" : "/aicontent/assets/icon_document.svg"}
                    alt=""
                  />
                  <span>문서 파일</span>
                </TypeButton>
              </TypeSelectionRow>

              {selectedType === 'text-image' && (
                <TextInputWrapper>
                  <TextInputArea
                    placeholder="제품 설명, 보도 자료, 핵심 내용 등을 입력하세요."
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    style={{ backgroundColor: 'transparent' }}
                  />
                </TextInputWrapper>
              )}

              <input
                id="file-input"
                type="file"
                hidden
                multiple
                accept={selectedType === 'text-image' ? "image/*" : ".pdf,.doc,.docx,.ppt,.pptx,.hwp,.hwpx,.xls,.xlsx,.txt,.rtf"}
                onChange={handleFileUpload}
              />

              <UploadRow>
                <UploadButton htmlFor="file-input">
                  <UploadIcon src="/aicontent/assets/icon_upload.svg" alt="" />
                  <UploadText>{selectedType === 'text-image' ? '이미지 업로드' : '문서 업로드'}</UploadText>
                </UploadButton>
              </UploadRow>

              {/* File Display Area: Empty State (+) or Thumbnails */}
              {filteredFiles.length === 0 ? (
                <AddThumbPlaceholder htmlFor="file-input">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </AddThumbPlaceholder>
              ) : (
                <ThumbnailGrid style={{ marginTop: '20px' }}>
                  {filteredFiles.map((file, idx) => (
                    <ThumbnailItem key={idx}>
                      {file.type.startsWith('image/') ? (
                        <ThumbImage src={file.data} alt={file.name} />
                      ) : (
                        <ThumbIconWrapper>
                          <img src="/aicontent/assets/icon_document.svg" width={24} height={24} alt="doc" />
                          <span style={{ marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{file.name}</span>
                        </ThumbIconWrapper>
                      )}
                      <RemoveThumbBtn onClick={() => removeFile(file)}>×</RemoveThumbBtn>
                    </ThumbnailItem>
                  ))}
                </ThumbnailGrid>
              )}
            </InputSection>

            <InputSection>
              <SectionLabel>원하시는 문서 스타일을 설정해 주세요.</SectionLabel>
              <SettingsRow>
                <SelectContainer onClick={() => { setOpenChannel(!openChannel); setOpenTone(false); }}>
                  <SelectValue>{channel || '채널 선택'}</SelectValue>
                  <SelectArrow src="/aicontent/assets/icon_arrow_down.svg" alt="" style={{ transform: openChannel ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                  {openChannel && (
                    <DropdownMenu>
                      {CHANNELS.map(c => (
                        <DropdownItem
                          key={c}
                          selected={channel === c}
                          onClick={(e) => {
                            e.stopPropagation();
                            setChannel(c);
                            setOpenChannel(false);
                          }}
                        >
                          {c}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </SelectContainer>

                <SelectContainer onClick={() => { setOpenTone(!openTone); setOpenChannel(false); }}>
                  <SelectValue>{tone || '톤앤매너'}</SelectValue>
                  <SelectArrow src="/aicontent/assets/icon_arrow_down.svg" alt="" style={{ transform: openTone ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                  {openTone && (
                    <DropdownMenu>
                      {TONES.map(t => (
                        <DropdownItem
                          key={t}
                          selected={tone === t}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTone(t);
                            setOpenTone(false);
                          }}
                        >
                          {t}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </SelectContainer>
              </SettingsRow>
            </InputSection>

            {loading && loadingBarData ? (
              <div
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', alignSelf: 'center' }}
              >
                <Lottie
                  animationData={loadingBarData}
                  loop={true}
                  style={{ width: '70%', marginTop: '-227px', marginBottom: '-289px' }}
                  rendererSettings={{
                    preserveAspectRatio: 'xMidYMid meet'
                  }}
                />
              </div>
            ) : (
              <GenerateButton onClick={handleGenerate} disabled={loading}>
                <GenerateIcon src="/aicontent/assets/icon_generate.svg" alt="" />
                <GenerateText>AI 초안 만들기</GenerateText>
              </GenerateButton>
            )}

          </FormContainer>
        </MainContent>

        {/* <Footer>
          <FooterLogo src="/aicontent/assets/footer_logo.svg" alt="PN Soft" />
          <Copyright>2026 Ⓒ PNSOFT ALL RIGHTS RESERVED</Copyright>
        </Footer> */}
      </PageWrapper>
    </>
  );
}
